import { createSelector } from '@ngrx/store';
import {
  selectBtPayContext,
  selectExternalUserUrn,
  selectIsBuyerOriginApp,
  selectIsPaymentLinks,
  selectVirtualOtherwisePrimaryTenantId,
} from '../session-store';
import {
  selectEmailVerificationPending,
  selectPaymentAccounts,
} from '../btpay';
import {
  PaymentRuleStore,
  selectIsCvvRequireRule,
  selectPaymentInclusionExclusionSettings,
} from '../payment-rules';
import { AccountPaymentMethod } from '@arc/unified-payments-frontend/shared-payment';

export const selectDeletePaymentAccountRequestData = createSelector(
  selectVirtualOtherwisePrimaryTenantId,
  selectBtPayContext,
  (tenantId, btPayContext) => {
    return {
      tenantId,
      btPayContext,
    };
  },
);

export const selectAllowPaymentAccountsDeletion = createSelector(
  selectIsBuyerOriginApp,
  (isBuyerApp) => !isBuyerApp,
);

export const selectAllowedPaymentAccounts = createSelector(
  selectPaymentAccounts,
  selectPaymentInclusionExclusionSettings,
  (paymentAccounts, paymentInclusionExclusion) => {
    if (paymentAccounts && !paymentInclusionExclusion.allowCreditCard) {
      paymentAccounts = paymentAccounts.filter(
        (x) =>
          x.paymentMethod !== AccountPaymentMethod.Card ||
          x.isOneTimePaymentAccount,
      );
    }
    return paymentAccounts;
  },
);

export const selectHasAllowedPaymentAccounts = createSelector(
  selectAllowedPaymentAccounts,
  (paymentAccounts) => paymentAccounts?.length > 0,
);

// Defer loading payment accounts for an anonymous payment-links buyer only
// while an email verification is genuinely pending (sign-in link sent and no
// error). The "no account with this username" error state must NOT block
// loading — the buyer can still proceed as guest, and skipping the load left
// the select-payment-method step empty (BTPAY-7020 blank screen).
export const selectCanLoadPaymentAccounts = createSelector(
  selectPaymentAccounts,
  selectIsPaymentLinks,
  selectExternalUserUrn,
  selectEmailVerificationPending,
  (paymentAccounts, isPaymentLinks, externalUserUrn, verificationPending) =>
    paymentAccounts == null &&
    (!isPaymentLinks || !!externalUserUrn || !verificationPending),
);

export const selectIsAlternativePaymentMethodAvailable = createSelector(
  PaymentRuleStore,
  (state) =>
    !!state?.isStripePermitted &&
    !!state?.settings?.payments?.acceptDirectDebit,
);

export const selectAllowedPaymentAccountsWithCvvOption = createSelector(
  selectAllowedPaymentAccounts,
  selectIsCvvRequireRule,
  (allowedPaymentAccounts, isCvvRequireRule) => {
    if (!allowedPaymentAccounts) {
      return null;
    }

    return allowedPaymentAccounts.map((account) => {
      const shouldRequestCvv =
        account.paymentMethod === AccountPaymentMethod.Card &&
        (account.isOneTimePaymentAccount ||
          isCvvRequireRule ||
          (!account.isCvvVerified && !isCvvRequireRule));
      account.shouldRequestCvv = shouldRequestCvv;
      return account;
    });
  },
);
